// L01 · The Checkbox (SPEC §3, Act I).
// The "I'm not a robot" checkbox dodges the pointer twice (120px, eased), lets you catch
// it on the third try, then apologizes. Always passes. Reduced-motion variant does not
// move — it asks "Are you sure?" once instead (CLAUDE.md §6).
//
// Trait (§3): `persistent` if the user chased it quickly, `patient` if they paused.

import { el } from "@/ui/chrome.ts";
import { runLevel } from "@/levels/util.ts";
import { L01 as V } from "@/content/voice.ts";
import type { Level, LevelContext, LevelResult } from "@/levels/types.ts";

// Two scripted dodge positions (transform, so layout never shifts and it cannot clip off
// the modal). The box returns home to be caught on the third attempt.
const DODGES = ["translate(116px, -6px)", "translate(-14px, 46px)"] as const;

// Under this median gap between attempts, the chase reads as eager → `persistent`.
const FAST_MS = 550;

interface Checkbox {
  root: HTMLElement;
  box: HTMLElement;
  setLabel(text: string): void;
  check(): void;
}

function buildCheckbox(): Checkbox {
  const root = el("div", "recap");

  const box = el("div", "recap__box");
  box.setAttribute("role", "checkbox");
  box.setAttribute("aria-checked", "false");
  // Accessible name comes from the visible label (kept in sync when setLabel changes it),
  // mirroring how the real reCAPTCHA checkbox is labelled.
  box.setAttribute("aria-labelledby", "recap-label");
  box.tabIndex = 0;

  const label = el("span", "recap__label");
  label.id = "recap-label";
  label.textContent = "I'm not a robot";

  const brand = el("div", "recap__brand");
  brand.innerHTML = "<b>reCAPTCHA</b>Privacy · Terms";

  root.append(box, label, brand);
  return {
    root,
    box,
    setLabel: (t) => (label.textContent = t),
    check: () => {
      box.style.transform = "none";
      box.classList.add("is-checked");
      box.textContent = "✓";
      box.setAttribute("aria-checked", "true");
    },
  };
}

/** Median inter-attempt gap → persistent (eager) vs patient (paused). */
function chaseTrait(times: number[]): string {
  if (times.length < 2) return V.slow;
  const gaps: number[] = [];
  for (let i = 1; i < times.length; i++) gaps.push(times[i]! - times[i - 1]!);
  gaps.sort((a, b) => a - b);
  const median = gaps[Math.floor(gaps.length / 2)]!;
  return median < FAST_MS ? V.fast : V.slow;
}

export const L01: Level = {
  id: "L01",
  act: 1,
  title: "The Checkbox",

  mount(host: HTMLElement, ctx: LevelContext): Promise<LevelResult> {
    return runLevel(ctx, (done) => {
      const cb = buildCheckbox();
      host.append(cb.root);

      const times: number[] = [];
      let settleTimer = 0;
      const succeed = (trait: string, line: string) => {
        times.push(performance.now());
        cb.check();
        // brief "verifying" beat, then hand off to the machine's line
        settleTimer = window.setTimeout(
          () => done({ pass: true, line, traits: [trait] }),
          ctx.reducedMotion ? 200 : 420,
        );
      };

      if (ctx.reducedMotion) {
        // No dodge. Ask once, confirm once.
        let asked = false;
        let askedAt = 0;
        const onActivate = () => {
          if (!asked) {
            asked = true;
            askedAt = performance.now();
            cb.setLabel(V.confirmLabel);
            return;
          }
          const quick = performance.now() - askedAt < 900;
          succeed(quick ? V.fast : V.slow, V.reduced);
        };
        cb.box.addEventListener("click", onActivate);
        cb.box.addEventListener("keydown", (e) => {
          if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            onActivate();
          }
        });
        return () => window.clearTimeout(settleTimer);
      }

      // Dodging path. On hover devices the box flees as the pointer arrives (the iconic
      // move); on touch it flees on the tap itself. Either way, two dodges then a catch.
      let dodges = 0;
      const dodge = () => {
        times.push(performance.now());
        cb.box.style.transform = DODGES[dodges] ?? DODGES[DODGES.length - 1]!;
        dodges++;
      };

      const hover =
        typeof matchMedia === "function" && matchMedia("(hover: hover)").matches;

      if (hover) {
        cb.box.addEventListener("mouseenter", () => {
          if (dodges < 2) dodge();
        });
        cb.box.addEventListener("click", () => {
          if (dodges >= 2) succeed(chaseTrait(times), V.apology);
        });
      } else {
        cb.box.addEventListener("pointerdown", (e) => {
          if (dodges < 2) {
            e.preventDefault(); // swallow the tap so it does not count as a catch
            dodge();
          } else {
            succeed(chaseTrait(times), V.apology);
          }
        });
      }
      // Keyboard: no pointer to dodge — let the space/enter user through after two nudges.
      cb.box.addEventListener("keydown", (e) => {
        if (e.key !== " " && e.key !== "Enter") return;
        e.preventDefault();
        if (dodges < 2) dodge();
        else succeed(chaseTrait(times), V.apology);
      });

      return () => window.clearTimeout(settleTimer);
    });
  },
};
